import type {CookieOptions} from './types';
import {isBrowser, isValidClickId} from './utils';

const COOKIE_NAME = 'bq_id';
const FALLBACK_KEY = 'biqli_attribution_v2';

interface StoredAttribution {
  clickId: string;
  expiresAt: number;
}

export class AttributionStorage {
  private readonly options: Required<Omit<CookieOptions, 'domain'>> & Pick<CookieOptions, 'domain'>;

  constructor(options: CookieOptions = {}) {
    this.options = {
      domain: options.domain?.trim() || undefined,
      path: options.path?.trim() || '/',
      sameSite: options.sameSite ?? 'lax',
      expiresInDays: Math.max(1, Math.min(365, options.expiresInDays ?? 90)),
    };
  }

  get(): string | null {
    if (!isBrowser()) return null;
    const cookieValue = this.readCookie();
    if (isValidClickId(cookieValue)) return cookieValue;

    const fallback = this.readFallback();
    if (!fallback) return null;
    if (fallback.expiresAt <= Date.now()) {
      this.clearFallback();
      return null;
    }
    return isValidClickId(fallback.clickId) ? fallback.clickId : null;
  }

  set(clickId: string): void {
    if (!isBrowser() || !isValidClickId(clickId)) return;
    const expiresAt = Date.now() + this.options.expiresInDays * 86_400_000;
    const segments = [
      `${COOKIE_NAME}=${encodeURIComponent(clickId)}`,
      `Expires=${new Date(expiresAt).toUTCString()}`,
      `Path=${this.options.path}`,
      `SameSite=${this.capitalize(this.options.sameSite)}`,
    ];
    if (this.options.domain) segments.push(`Domain=${this.options.domain}`);
    if (window.location.protocol === 'https:' || this.options.sameSite === 'none') segments.push('Secure');
    document.cookie = segments.join('; ');

    if (this.readCookie() === clickId) this.clearFallback();
    else this.writeFallback({clickId, expiresAt});
  }

  clear(): void {
    if (!isBrowser()) return;
    const segments = [
      `${COOKIE_NAME}=`,
      'Expires=Thu, 01 Jan 1970 00:00:00 GMT',
      `Path=${this.options.path}`,
      `SameSite=${this.capitalize(this.options.sameSite)}`,
    ];
    if (this.options.domain) segments.push(`Domain=${this.options.domain}`);
    if (window.location.protocol === 'https:' || this.options.sameSite === 'none') segments.push('Secure');
    document.cookie = segments.join('; ');
    this.clearFallback();
  }

  private readCookie(): string | null {
    const prefix = `${COOKIE_NAME}=`;
    const segment = document.cookie
      .split(';')
      .map(value => value.trim())
      .find(value => value.startsWith(prefix));
    if (!segment) return null;
    try {
      return decodeURIComponent(segment.slice(prefix.length));
    } catch {
      return null;
    }
  }

  private readFallback(): StoredAttribution | null {
    try {
      const raw = window.localStorage.getItem(FALLBACK_KEY);
      const value = raw ? JSON.parse(raw) as Partial<StoredAttribution> : null;
      return value && typeof value.clickId === 'string' && typeof value.expiresAt === 'number'
        ? {clickId: value.clickId, expiresAt: value.expiresAt}
        : null;
    } catch {
      return null;
    }
  }

  private writeFallback(value: StoredAttribution): void {
    try {
      window.localStorage.setItem(FALLBACK_KEY, JSON.stringify(value));
    } catch {
      // Keep attribution in memory when persistent storage is unavailable.
    }
  }

  private clearFallback(): void {
    try {
      window.localStorage.removeItem(FALLBACK_KEY);
    } catch {
      // Browser storage may be disabled.
    }
  }

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}
